import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { debounceTime, Subscription } from 'rxjs';
import { TicketIntegrationNoteItem } from 'src/app/api/models';
import { TicketControllerFindIntegrationNotes$Params } from 'src/app/api/fn/ticket/ticket-controller-find-integration-notes';
import { TicketService } from 'src/app/api/services';

// Note per le integrazioni di un ticket: elenco con filtri e, se il ticket non è chiuso né
// annullato, inserimento di una nuova nota
@Component({
  selector: 'app-modal-ticket-integration-notes',
  templateUrl: './modal-ticket-integration-notes.component.html',
})
export class ModalTicketIntegrationNotesComponent implements OnInit, OnDestroy {
  readonly maxNoteLength = 4000;
  readonly rowsPerPage = 10;
  readonly syncedOptions = [
    { label: 'Tutte',            value: null        },
    { label: 'Sincronizzate',    value: true        },
    { label: 'Da sincronizzare', value: false       },
    { label: 'Da integrazione', value: 'from_hda'  },
  ];

  ticketId: number;
  ticketLabel: string;
  canAdd: boolean;

  notes: TicketIntegrationNoteItem[] = [];
  total = 0;
  first = 0;
  loading = false;
  saving = false;

  filters = new FormGroup({
    query: new FormControl<string | null>(null),
    author: new FormControl<string | null>(null),
    synced: new FormControl<boolean | string | null>(null),
    range: new FormControl<Date[] | null>(null),
  });

  newNote = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(this.maxNoteLength)],
  });

  private filtersSub?: Subscription;

  constructor(
    private config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private ticketService: TicketService,
    private messageService: MessageService
  ) {
    this.ticketId = config.data.ticket_id;
    this.ticketLabel = config.data.ticket_label;
    this.canAdd = !!config.data.can_add;
  }

  ngOnInit() {
    this.load();
    // i filtri si applicano da soli (con una breve attesa mentre si scrive) e riportano alla prima pagina
    this.filtersSub = this.filters.valueChanges.pipe(debounceTime(400)).subscribe(() => {
      this.first = 0;
      this.load();
    });
  }

  ngOnDestroy() {
    this.filtersSub?.unsubscribe();
  }

  get hasFilters(): boolean {
    const v = this.filters.value;
    return !!(v.query || v.author || v.synced !== null || (v.range && v.range?.[0]));
  }

  clearFilters() {
    this.filters.reset({ query: null, author: null, synced: null, range: null });
  }

  authorName(note: TicketIntegrationNoteItem): string {
    if ((note as any).from_hda) return 'HDA';
    const u = note.related_user;
    return [u?.name, u?.last_name].filter(Boolean).join(' ') || u?.username || '-';
  }

  onPage(event: { first?: number }) {
    this.first = event.first ?? 0;
    this.load();
  }

  load() {
    const v = this.filters.value;
    const isFromHda = v.synced === 'from_hda';
    const params: TicketControllerFindIntegrationNotes$Params = {
      id: this.ticketId,
      limit: this.rowsPerPage,
      skip: this.first,
      query: v.query?.trim() || undefined,
      author: v.author?.trim() || undefined,
      synced: isFromHda ? undefined : (v.synced as boolean | null | undefined) ?? undefined,
      from_hda: isFromHda ? true : undefined,
    };
    // intervallo date (giorni interi, nel fuso del browser); con un solo giorno scelto vale quel giorno
    const [from, to] = v.range ?? [];
    if (from) {
      const start = new Date(from);
      start.setHours(0, 0, 0, 0);
      const end = new Date(to ?? from);
      end.setHours(23, 59, 59, 999);
      params.created_from = start.toISOString();
      params.created_to = end.toISOString();
    }

    this.loading = true;
    this.ticketService.ticketControllerFindIntegrationNotes(params).subscribe({
      next: res => {
        this.notes = res.data?.list ?? [];
        this.total = res.data?.count ?? 0;
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Note integrazione',
          detail:
            typeof err?.error?.message === 'string'
              ? err.error.message
              : 'Impossibile caricare le note.',
        });
      },
    });
  }

  addNote() {
    const note = this.newNote.value.trim();
    if (!note || this.newNote.invalid || this.saving) return;
    this.saving = true;
    this.ticketService
      .ticketControllerCreateIntegrationNote({ id: this.ticketId, body: { note } })
      .subscribe({
        next: () => {
          this.saving = false;
          this.newNote.reset('');
          // la nota nuova è la più recente: si torna alla prima pagina
          this.first = 0;
          this.load();
          this.messageService.add({
            severity: 'success',
            summary: 'Note integrazione',
            detail: 'Nota aggiunta.',
          });
        },
        error: err => {
          this.saving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Note integrazione',
            detail:
              typeof err?.error?.message === 'string'
                ? err.error.message
                : 'Impossibile aggiungere la nota.',
          });
        },
      });
  }

  close() {
    this.ref.close();
  }
}
